import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

const BANNER_TEXT = 'Questo Sito utilizza soltanto cookie tecnici, necessari per il corretto funzionamento e per ricordare la scelta espressa in questo banner. Non sono installati cookie statistici né di profilazione. La X in alto a destra chiude il banner e lascia attivi solo i cookie tecnici. Accetta tutti conferma questa impostazione. Personalizza mostra l’elenco dei cookie. Per saperne di più consulta la';

const DECLARATION = [
  {
    name: 'fgt_cookie_consent',
    vendor: 'FitGymTrack (prima parte)',
    category: 'Tecnico',
    purpose: 'Memorizza la scelta espressa nel banner. Non richiede consenso.',
    duration: '6 mesi',
  },
];

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition ${
        checked ? 'bg-[#372d83]' : 'bg-gray-300'
      } ${disabled ? 'opacity-80 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export function LegalLinks({ className = '', linkClassName = '' }) {
  const { openPreferences } = useCookieConsent();
  const link = `underline-offset-2 hover:underline ${linkClassName}`;

  return (
    <span className={className}>
      <Link to="/privacy-policy" className={link} title="Privacy Policy">
        Privacy Policy
      </Link>
      <span className="mx-2 opacity-60">·</span>
      <Link to="/cookie-policy" className={link} title="Cookie Policy">
        Cookie Policy
      </Link>
      <span className="mx-2 opacity-60">·</span>
      <button type="button" onClick={openPreferences} className={link}>
        Gestisci preferenze cookie
      </button>
    </span>
  );
}

export default function CookieConsentLayer() {
  const {
    bannerOpen,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useCookieConsent();
  const titleId = useId();
  const prefsTitleId = useId();

  useEffect(() => {
    if (!preferencesOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') closePreferences();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [preferencesOpen, closePreferences]);

  return (
    <>
      {bannerOpen && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          className="fixed z-[80] bottom-20 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-28 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-[#dad8e5] bg-white text-gray-800 shadow-2xl">
            <div className="flex justify-end px-3 pt-3">
              <button
                type="button"
                onClick={rejectNonEssential}
                aria-label="Rifiuta i cookie non necessari"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 pb-5 sm:px-6 sm:pb-6 -mt-1">
              <h2 id={titleId} className="sr-only">Informativa cookie</h2>
              <p className="text-sm leading-6">
                {BANNER_TEXT}{' '}
                <Link to="/privacy-policy" className="font-medium text-[#372d83] underline underline-offset-2">
                  Privacy Policy
                </Link>
                .
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  type="button"
                  onClick={openPreferences}
                  className="rounded-xl border border-[#372d83] px-5 py-2.5 text-sm font-semibold text-[#372d83] hover:bg-[#372d83]/5"
                >
                  Personalizza
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-xl bg-[#372d83] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2c2469]"
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {preferencesOpen && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-3 sm:p-6">
          <button
            type="button"
            aria-label="Chiudi preferenze cookie"
            className="absolute inset-0 bg-black/50"
            onClick={closePreferences}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={prefsTitleId}
            className="relative flex max-h-[min(92vh,820px)] w-full max-w-2xl flex-col rounded-2xl bg-white text-gray-800 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div>
                <h2 id={prefsTitleId} className="text-lg font-bold text-[#372d83]">
                  Personalizza i cookie
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Sul sito c’è solo il cookie tecnico che ricorda questa scelta.
                </p>
              </div>
              <button
                type="button"
                onClick={closePreferences}
                aria-label="Chiudi"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4 space-y-3">
              <Category
                title="Tecnici e funzionali"
                description="Servono al funzionamento del sito e a ricordare questa scelta. Non richiedono consenso."
                checked
                disabled
                onChange={() => {}}
              />

              <div className="pt-2">
                <h3 className="text-sm font-semibold text-[#372d83]">Dichiarazione cookie</h3>
                <div className="mt-2 overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Strumento</th>
                        <th className="px-3 py-2 font-medium">Fornitore</th>
                        <th className="px-3 py-2 font-medium">Categoria</th>
                        <th className="px-3 py-2 font-medium">Durata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DECLARATION.map((row) => (
                        <tr key={row.name} className="border-t border-gray-100 align-top">
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {row.name}
                            <span className="mt-0.5 block font-normal text-gray-500">{row.purpose}</span>
                          </td>
                          <td className="px-3 py-2">{row.vendor}</td>
                          <td className="px-3 py-2">{row.category}</td>
                          <td className="px-3 py-2">{row.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">
                <Link to="/privacy-policy" className="underline underline-offset-2 text-[#372d83]">Privacy Policy</Link>
                <span className="mx-1.5">·</span>
                <Link to="/cookie-policy" className="underline underline-offset-2 text-[#372d83]">Cookie Policy</Link>
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={savePreferences}
                className="rounded-xl border border-[#372d83] px-5 py-2.5 text-sm font-semibold text-[#372d83] hover:bg-[#372d83]/5"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="rounded-xl bg-[#372d83] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2c2469]"
              >
                Accetta tutti
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

function Category({ title, description, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-1 text-xs leading-5 text-gray-600">{description}</p>
      </div>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} label={title} />
    </div>
  );
}
